import { useFrame } from '@react-three/fiber';
import { memo, useEffectEvent, useLayoutEffect, useMemo, useRef } from 'react';
import seedrandom from 'seedrandom';
import * as THREE from 'three';
import type { HexTileMapKey } from '@/game/hexmap';
import {
  axialHexDistance,
  axialToWorld,
  getHexTileMapKey,
  hexesWithinRadius,
  hexMapConfig,
} from '@/game/hexmap';
import type { Fog } from '@/game/view/fog';
import { useFog } from './app/simFrame';

/**
 * Fog of war drawn as clouds: a few low-poly puffs over every unexplored
 * tile, thinning out over the ocean past the coast so the cloud bank has no
 * hard edge. Puffs drift and bob in the vertex shader (no per-frame CPU
 * work); when a tile is explored its puffs fade out, shrinking quickly at
 * the end. The bank only draws live puffs: an explored tile's puffs are
 * swap-removed and the instance count shrinks, so exploring cuts GPU work.
 * Not raycastable, so clicks and hover reach the tiles underneath.
 */

/** Puffs per tile at full quality; Low quality passes fewer (`puffsPerTile`), each larger. */
const PUFFS_PER_TILE = 4;
/** Cloud cover continues this many hexes past the map edge, thinning out. */
const OCEAN_MARGIN = 7;
const REVEAL_SECONDS = 1.1;
/** Height of the cloud bank's centre above the ground. */
const CLOUD_HEIGHT = 0.45;
const ORIGIN = { q: 0, r: 0 };
const TILE_RADIUS = hexMapConfig.hexMap.tile.radius;

// Detail 0 (20 faces): flat-shaded and flattened, it still reads as a puff.
const geometry = new THREE.IcosahedronGeometry(1, 0);
const noRaycast = () => null;

interface Puff {
  key: HexTileMapKey;
  position: THREE.Vector3;
  scale: THREE.Vector3;
}

function buildPuffs(
  mapRadius: number,
  perTile: number
): { puffs: Puff[]; byKey: Map<HexTileMapKey, number[]> } {
  // Fewer puffs grow to keep the bank closed.
  const grow = perTile < PUFFS_PER_TILE ? 1.2 : 1;
  const puffs: Puff[] = [];
  const byKey = new Map<HexTileMapKey, number[]>();
  for (const cell of hexesWithinRadius(ORIGIN, mapRadius + OCEAN_MARGIN)) {
    const key = getHexTileMapKey(cell);
    const world = axialToWorld(cell, TILE_RADIUS);
    // Full cover over the map, tapering to nothing across the ocean margin.
    const beyond = axialHexDistance(ORIGIN, cell) - mapRadius;
    const cover = beyond <= 0 ? 1 : 1 - THREE.MathUtils.smoothstep(beyond, 0, OCEAN_MARGIN);
    if (cover <= 0.05) continue;
    const rng = seedrandom(`cloud:${key}`);
    const indices: number[] = [];
    for (let i = 0; i < perTile; i++) {
      const angle = rng() * Math.PI * 2;
      const dist = Math.sqrt(rng()) * 0.5 * TILE_RADIUS;
      const size = (0.55 + rng() * 0.35) * TILE_RADIUS * cover * grow;
      indices.push(puffs.length);
      puffs.push({
        key,
        position: new THREE.Vector3(
          world.x + Math.cos(angle) * dist,
          CLOUD_HEIGHT + (rng() - 0.5) * 0.25,
          world.z + Math.sin(angle) * dist
        ),
        // Flattened so the bank reads as a soft layer rather than balls.
        scale: new THREE.Vector3(size * 1.2, size * 0.5, size * 1.05),
      });
    }
    byKey.set(key, indices);
  }
  return { puffs, byKey };
}

/**
 * Cloud material. `fading` adds a per-instance `aOpacity` attribute and draws
 * transparently; the main bank stays opaque so its puffs never mis-sort.
 */
function makeMaterial(time: { value: number }, fading: boolean): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    // Self-lit a little so shaded sides stay soft grey-blue, not dark.
    emissive: '#aab4c2',
    emissiveIntensity: 0.55,
    flatShading: true,
    roughness: 1,
    // Not tone-mapped, so sunlit tops stay as white as the background.
    toneMapped: false,
    transparent: fading,
    depthWrite: !fading,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = time;
    shader.vertexShader = `uniform float uTime;
${fading ? 'attribute float aOpacity;\nvarying float vOpacity;' : ''}
${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
#ifdef USE_INSTANCING
      // Slow drift and bob, phased by where the puff sits.
      vec3 cloudPos = instanceMatrix[3].xyz;
      float cloudPhase = cloudPos.x * 0.7 + cloudPos.z * 0.5;
      transformed.y += sin(uTime * 0.5 + cloudPhase) * 0.12;
      transformed.x += sin(uTime * 0.2 + cloudPhase * 1.3) * 0.15;
#endif
${fading ? 'vOpacity = aOpacity;' : ''}`
    );
    if (fading) {
      shader.fragmentShader = `varying float vOpacity;\n${shader.fragmentShader}`.replace(
        '#include <color_fragment>',
        '#include <color_fragment>\n      diffuseColor.a *= vOpacity;'
      );
    }
  };
  return material;
}

/** Opacity and size of a dissolving puff, t from 0 (start) to 1 (gone). */
function fadeAt(t: number): { opacity: number; scale: number } {
  // Fades out first; the quick shrink kicks in once it's mostly transparent.
  return {
    opacity: 1 - THREE.MathUtils.smoothstep(t, 0, 0.85),
    scale: 1 - 0.7 * THREE.MathUtils.smoothstep(t, 0.5, 1),
  };
}

interface FogCloudsProps {
  mapRadius: number;
  /** Puffs per tile (default 4; Low quality uses 2). */
  puffsPerTile?: number;
}

/** Clouds over the tiles the player hasn't explored; re-renders only on a fog change. */
export const FogClouds = memo(function FogClouds({
  mapRadius,
  puffsPerTile = PUFFS_PER_TILE,
}: FogCloudsProps) {
  const fog = useFog();
  const { puffs, byKey } = useMemo(
    () => buildPuffs(mapRadius, puffsPerTile),
    [mapRadius, puffsPerTile]
  );
  const time = useMemo(() => ({ value: 0 }), []);
  const material = useMemo(() => makeMaterial(time, false), [time]);
  const fadingMaterial = useMemo(() => makeMaterial(time, true), [time]);
  // Own geometry copy for the fading mesh, carrying per-instance opacity.
  const fadingGeometry = useMemo(() => {
    const g = geometry.clone();
    g.setAttribute(
      'aOpacity',
      new THREE.InstancedBufferAttribute(new Float32Array(Math.max(1, puffs.length)), 1)
    );
    return g;
  }, [puffs]);
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const fadingRef = useRef<THREE.InstancedMesh>(null);
  /** Tiles whose clouds are currently shown. */
  const cloudedRef = useRef(new Set<HexTileMapKey>());
  // The bank's live puffs fill slots 0..count-1: puff index → slot (-1: gone)
  // and slot → puff index.
  const slotOfRef = useRef(new Int32Array(0));
  const puffAtRef = useRef(new Int32Array(0));
  /** Tiles whose clouds are dissolving, with the time it started. */
  const revealingRef = useRef(new Map<HexTileMapKey, number>());
  const clockRef = useRef(0);

  const dummy = useMemo(() => new THREE.Object3D(), []);
  const writePuff = (mesh: THREE.InstancedMesh, slot: number, i: number, scale: number) => {
    const puff = puffs[i];
    if (!puff) return;
    dummy.position.copy(puff.position);
    dummy.scale.copy(puff.scale).multiplyScalar(scale);
    dummy.updateMatrix();
    mesh.setMatrixAt(slot, dummy.matrix);
  };

  // Takes a puff out of the bank: the last live puff moves into its slot.
  const removePuff = (mesh: THREE.InstancedMesh, i: number) => {
    const slotOf = slotOfRef.current;
    const puffAt = puffAtRef.current;
    const slot = slotOf[i];
    if (slot < 0) return;
    const last = mesh.count - 1;
    if (slot !== last) {
      const moved = puffAt[last];
      writePuff(mesh, slot, moved, 1);
      puffAt[slot] = moved;
      slotOf[moved] = slot;
    }
    slotOf[i] = -1;
    mesh.count = last;
  };

  // Initial layout: cloud every tile that's unexplored right now (no
  // animation). Reads the fog as it is, but only a new layout re-runs it.
  const layOut = useEffectEvent((byKey: ReadonlyMap<HexTileMapKey, number[]>) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    cloudedRef.current.clear();
    revealingRef.current.clear();
    const slotOf = new Int32Array(puffs.length).fill(-1);
    const puffAt = new Int32Array(puffs.length);
    let live = 0;
    for (const [key, indices] of byKey) {
      if (fog.sight(key) !== 'unexplored') continue;
      cloudedRef.current.add(key);
      for (const i of indices) {
        writePuff(mesh, live, i, 1);
        slotOf[i] = live;
        puffAt[live] = i;
        live++;
      }
    }
    slotOfRef.current = slotOf;
    puffAtRef.current = puffAt;
    mesh.count = live;
    mesh.instanceMatrix.needsUpdate = true;
  });
  // `byKey` and `puffs` come from one layout, so a new `byKey` is a new layout.
  useLayoutEffect(() => layOut(byKey), [byKey]);

  // Vision changed: newly explored tiles leave the bank and start dissolving.
  const reveal = useEffectEvent((fog: Fog) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    let changed = false;
    for (const key of cloudedRef.current) {
      if (fog.sight(key) !== 'unexplored') {
        cloudedRef.current.delete(key);
        revealingRef.current.set(key, clockRef.current);
        for (const i of byKey.get(key) ?? []) removePuff(mesh, i);
        changed = true;
      }
    }
    if (changed) mesh.instanceMatrix.needsUpdate = true;
  });
  useLayoutEffect(() => reveal(fog), [fog]);

  useFrame(({ clock }) => {
    const now = clock.getElapsedTime();
    clockRef.current = now;
    time.value = now;
    const fading = fadingRef.current;
    if (!fading) return;
    const opacity = fadingGeometry.getAttribute('aOpacity') as THREE.InstancedBufferAttribute;
    let slot = 0;
    for (const [key, start] of revealingRef.current) {
      const t = Math.min(1, (now - start) / REVEAL_SECONDS);
      if (t >= 1) {
        revealingRef.current.delete(key);
        continue;
      }
      const f = fadeAt(t);
      for (const i of byKey.get(key) ?? []) {
        writePuff(fading, slot, i, f.scale);
        opacity.setX(slot, f.opacity);
        slot++;
      }
    }
    if (fading.count === 0 && slot === 0) return;
    fading.count = slot;
    fading.instanceMatrix.needsUpdate = true;
    opacity.needsUpdate = true;
  });

  return (
    <>
      <instancedMesh
        ref={meshRef}
        args={[geometry, material, Math.max(1, puffs.length)]}
        frustumCulled={false}
        raycast={noRaycast}
      />
      <instancedMesh
        ref={fadingRef}
        args={[fadingGeometry, fadingMaterial, Math.max(1, puffs.length)]}
        count={0}
        frustumCulled={false}
        raycast={noRaycast}
      />
    </>
  );
});
